import type React from 'react';
import { useIntl } from 'react-intl';

import type { ButtonProps } from '@xing-com/button';
import { useLocation } from '@xing-com/crate-core-router';
import { CarouselSection } from '@xing-com/crate-x4e-carousel-section';
import { EntityCard } from '@xing-com/crate-x4e-entity-card';
import { useServerData } from '@xing-com/crate-xinglet';
import { Container } from '@xing-com/layout-primitives';
import type { MarkerProps } from '@xing-com/marker';
import { scale430, xdlColorBackgroundSecondary } from '@xing-com/tokens';

import { StyledAnchor } from '../card/card.styles';
import { shuffle } from '../shuffle';
import { StyledCarouselSection } from '../startpage-logged-out.styles';

import { CompaniesData } from './data/companies-data';

const tracking = {
  companies: {
    card: 'losp_employer_card_click',
    button: 'losp_employer_card_jobs_button_click',
  },
};

export const CompaniesSection: React.FC = () => {
  const { search } = useLocation();
  const { formatMessage } = useIntl();
  const queryParams = new URLSearchParams(search);
  const seed = useServerData('companiesStartPageSeed', Math.random());
  const companies =
    queryParams.get('allCompanies') === 'true'
      ? CompaniesData.sort((a, b) => a.position - b.position)
      : shuffle(CompaniesData, seed).slice(0, 20);

  // TEMPORARY ROLLBACK SEE : https://teams.microsoft.com/l/message/19:e78b1881cf1643a19117fbdd341d11c8@thread.tacv2/1732724274916?tenantId=a0e43afe-ad2b-4714-8244-a7b82df24eb4&groupId=c264f279-01a7-47f1-8b04-b0234470a58c&parentMessageId=1732724274916&teamName=XING%20Talent%20Acquisition%20Cluster&channelName=Support&createdTime=1732724274916

  // const { data } = useQuery(StartPageCompaniesDocument, {
  //   variables: {
  //     ids: CompaniesData.map(({ id }) => id),
  //   },
  // });
  //
  // const companiesJobsCount =
  //   data?.companies?.map((company) => ({
  //     jobsCount: company?.jobsCount ?? 0,
  //     id: company?.id,
  //   })) ?? [];

  // const serverData = initialValue.map((company) => ({
  //   ...company,
  //   jobsCount: companiesJobsCount.find(({ id }) => id === company.id)
  //     ?.jobsCount,
  // }));

  const handleTracking = ({
    url,
    trackingId,
    cardPosition,
  }: {
    url: string;
    trackingId: string;
    cardPosition: number;
  }): void => {
    const fullUrl = `${url}?sc_o=${trackingId}&sc_o_PropActionOrigin=losp_recruiter_and_employer_card_position_${cardPosition}`;
    window.location.assign(fullUrl);
  };

  const defaultMarker: MarkerProps = {
    variant: 'highlight',
    children: formatMessage({
      id: 'RECRUITERS_SECTION_FLAG_COMPANIES',
    }),
  };

  return (
    <StyledCarouselSection $bgColor={xdlColorBackgroundSecondary}>
      <Container variant="expanded">
        <CarouselSection
          titleKey="CRATE_LOSP_COMPANIES_SECTION_TITLE"
          subtitleKey="CRATE_LOSP_COMPANIES_SECTION_SUBTITLE"
          itemWidth="346px"
          itemMinWidth="346px"
          itemMaxWidth="346px"
          // @ts-expect-error FIXME: SC6
          itemHeight={scale430}
          itemMinHeight={scale430}
          itemMaxHeight={scale430}
          itemSnapPosition="center"
          useFullWidth={true}
        >
          {companies.map((cardData, index) => {
            const buttonUrl = `${cardData.profileUrl}/jobs`;
            const buttonProps: ButtonProps = {
              variant: 'tertiary',
              // @ts-expect-error FIXME: SC6
              onClick: (event: MouseEvent) => {
                event.preventDefault();
                event.stopPropagation();

                handleTracking({
                  cardPosition: index + 1,
                  url: buttonUrl,
                  trackingId: tracking.companies.button,
                });
              },
              children: formatMessage(
                {
                  id:
                    // TEMPORARY ROLLBACK
                    // cardData.jobsCount && cardData.jobsCount === 1
                    //   ? 'CP_TOP_COMPANIES_CARD_BUTTON.one'
                    //   : 'CP_TOP_COMPANIES_CARD_BUTTON.other',
                    'CP_TOP_COMPANIES_CARD_BUTTON.other',
                },
                // TEMPORARY ROLLBACK
                // { value: cardData.jobsCount ? cardData.jobsCount : '' }
                { value: '' }
              ),
            };

            const markers = [defaultMarker];

            const image = {
              src: cardData.photoUrl,
            };

            const onCardClick = (event: React.MouseEvent): void => {
              event.preventDefault();
              event.stopPropagation();

              handleTracking({
                cardPosition: index + 1,
                url: cardData.profileUrl,
                trackingId: tracking.companies.card,
              });
            };

            return (
              <StyledAnchor
                key={cardData.id}
                href={cardData.profileUrl}
                onClick={onCardClick}
              >
                <EntityCard
                  headline={cardData.name}
                  copy1={cardData.jobTitle}
                  profileImage={image}
                  markers={markers}
                  button={buttonProps}
                />
              </StyledAnchor>
            );
          })}
        </CarouselSection>
      </Container>
    </StyledCarouselSection>
  );
};
